import { EDITOR_TYPES } from "../constants/index.js";

// A saved design carries the size it was saved at (editorDetails.width/height).
// The customer can change the size in the cart AFTERWARDS, and nothing rewrites
// editorDetails, so on the next open the cart is the source of truth and the
// saved pages get rescaled to it. scaleLayout derives its factors from each
// layout's OWN stored width/height, not from editorDetails, so re-opening
// without saving re-derives the same result rather than compounding.
//
// Lives in its own module (rather than inline in useInitializeProject) so the
// comparison can be tested without pulling react-router and axios into jest.
// This is the only branch that can silently change what gets printed.
//
// ponytail: plain stretch, not a re-layout. Width and height scale
// independently, so a same-aspect size change is clean but 12x8 → 8x11 distorts
// frames and mis-crops photos. Add an aspect-aware re-layout (or a "size
// changed, review your design" prompt) if that becomes a real product need.
export const resolveProjectSize = ({
  editorDetails,
  cartDetails,
  editorType,
  isCustomer,
}) => {
  const fromEditor = {
    width: Number(editorDetails.width),
    height: Number(editorDetails.height),
    depth: editorDetails.depth,
    safeMargin: editorDetails?.safe_margin || 0,
    bleedMargin: editorDetails?.bleed_margin || 0,
    sizeChanged: false,
  };

  const cartSize = cartDetails?.size_id;
  // Admins can resize in-editor via Size Settings, which does NOT write back to
  // the cart; letting the cart win would silently revert their resize on the
  // next load. Customers have no in-editor resize, so the cart is authoritative.
  if (!isCustomer || !cartSize) return fromEditor;

  // Photobook/layflat carts store the single-page width; canvas space is the
  // full spread. Mirrors the no-saved-design branch in useInitializeProject.
  const spread =
    editorType === EDITOR_TYPES.PHOTOBOOK ||
      editorType === EDITOR_TYPES.LAYFLATALBUM
      ? 2
      : 1;

  // Number() on both sides: editorDetails dimensions come back as strings on
  // some records, and "2400" !== 2400 would fire on every single load.
  // 1px tolerance absorbs rounding, not a real size change.
  const cartWidth = Number(cartSize.width) * spread;
  const cartHeight = Number(cartSize.height);
  if (!Number.isFinite(cartWidth) || !Number.isFinite(cartHeight)) {
    return fromEditor;
  }

  const sizeChanged =
    Math.abs(cartWidth - fromEditor.width) > 1 ||
    Math.abs(cartHeight - fromEditor.height) > 1;
  if (!sizeChanged) return fromEditor;

  return {
    width: cartWidth,
    height: cartHeight,
    depth: cartSize.depth || 0,
    safeMargin: cartSize.safe_margin || 0,
    bleedMargin: cartSize.bleed_margin || 0,
    sizeChanged: true,
  };
};
